export enum CourseStatus {
  Draft = 1,
  Published = 2,
  Archived = 3,
}

export enum CourseVisibility {
  Tenant = 1,
  Restricted = 2,
  Hidden = 3,
}

export enum CourseLevel {
  AllLevels = 0,
  Beginner = 1,
  Intermediate = 2,
  Advanced = 3,
}

export enum LessonType {
  RichText = 1,
  Video = 2,
  File = 3,
  Embed = 4,
  Lecture = 10,
  Exam = 11,
  Quiz = 12,
  Assignment = 13,
  LessonPresentation = 14,
  ClassroomSession = 15,
}

export enum VideoSourceType {
  Upload = 1,
  YouTube = 2,
  Vimeo = 3,
}

// ── L2: enrollment & progress ──────────────────────────────────────────────
export enum EnrollmentSource {
  GradeGroupAuto = 1,
  Manual = 2,
  SelfEnrollCode = 3,
  Approval = 4,
  SelfEnroll = 5,
  /** L5: opened by a learning path enrollment. */
  LearningPath = 6,
}

export enum EnrollmentStatus {
  Pending = 1,
  Active = 2,
  Completed = 3,
  Dropped = 4,
}

export enum LessonProgressStatus {
  NotStarted = 0,
  InProgress = 1,
  Completed = 2,
}

export enum LessonLockReason {
  None = 0,
  NotEnrolled = 1,
  ReleaseDate = 2,
  DaysAfterEnrollment = 3,
  PreviousLesson = 4,
  PreviousModule = 5,
}

export type CompletionMode = 'manual' | 'video' | 'assessment';

export const LINK_LESSON_TYPES: LessonType[] = [
  LessonType.Lecture,
  LessonType.Exam,
  LessonType.Quiz,
  LessonType.Assignment,
  LessonType.LessonPresentation,
  LessonType.ClassroomSession,
];

export function isLinkType(type: LessonType): boolean {
  return type >= 10;
}

/** i18n key suffix + bootstrap icon per lesson type. */
export const LESSON_TYPE_META: Record<LessonType, { key: string; icon: string }> = {
  [LessonType.RichText]: { key: 'RichText', icon: 'bi-file-earmark-text' },
  [LessonType.Video]: { key: 'Video', icon: 'bi-play-btn' },
  [LessonType.File]: { key: 'File', icon: 'bi-file-earmark-pdf' },
  [LessonType.Embed]: { key: 'Embed', icon: 'bi-window' },
  [LessonType.Lecture]: { key: 'Lecture', icon: 'bi-camera-video' },
  [LessonType.Exam]: { key: 'Exam', icon: 'bi-clipboard-check' },
  [LessonType.Quiz]: { key: 'Quiz', icon: 'bi-patch-question' },
  [LessonType.Assignment]: { key: 'Assignment', icon: 'bi-journal-check' },
  [LessonType.LessonPresentation]: { key: 'LessonPresentation', icon: 'bi-easel' },
  [LessonType.ClassroomSession]: { key: 'ClassroomSession', icon: 'bi-broadcast' },
};

export interface CourseCategoryDto {
  id: string;
  nameEn: string;
  nameAr: string;
  sortOrder: number;
  courseCount: number;
}

export interface UpsertCourseCategoryDto {
  id?: string | null;
  nameEn: string;
  nameAr: string;
  sortOrder: number;
}

export interface CourseSearchDto {
  pageNumber: number;
  pageSize: number;
  search?: string | null;
  status?: CourseStatus | null;
  categoryId?: string | null;
  gradeId?: string | null;
  /** Student catalog: only enrolled courses. */
  enrolledOnly?: boolean;
}

export interface CourseEnrollmentStateDto {
  id: string;
  status: EnrollmentStatus;
  source: EnrollmentSource;
  requestedAt: string;
  enrolledAt?: string | null;
  completedAt?: string | null;
  progressPercent: number;
  completedLessons: number;
  totalLessons: number;
  lastLessonId?: string | null;
  lastActivityAt?: string | null;
}

export interface LessonLockDto {
  reason: LessonLockReason;
  availableAt?: string | null;
  blockingId?: string | null;
  blockingTitle?: string | null;
}

/** Drip / prerequisite settings shared by modules and lessons. */
export interface UnlockRuleFields {
  releaseAt?: string | null;
  releaseAfterDays?: number | null;
  requiresPrevious: boolean;
}

export interface CourseListItemDto {
  id: string;
  title: string;
  summary?: string | null;
  coverFileId?: string | null;
  categoryId?: string | null;
  categoryNameEn?: string | null;
  categoryNameAr?: string | null;
  tags: string[];
  level: CourseLevel;
  language: string;
  gradeId?: string | null;
  groupId?: string | null;
  ownerId: string;
  ownerNameEn?: string | null;
  ownerNameAr?: string | null;
  status: CourseStatus;
  visibility: CourseVisibility;
  moduleCount: number;
  lessonCount: number;
  totalMinutes: number;
  /** L4: average of visible reviews (0 when none). */
  averageRating: number;
  ratingCount: number;
  canManage: boolean;
  /** Owner or tenant admin: may delete the course and change its co-teachers. */
  canDelete?: boolean;
  publishedAt?: string | null;
  updatedAt: string;
  /** Student views: own enrollment + progress. */
  enrollment?: CourseEnrollmentStateDto | null;
}

export interface LessonSummaryDto {
  id: string;
  moduleId: string;
  title: string;
  type: LessonType;
  sortOrder: number;
  estimatedMinutes?: number | null;
  isPreview: boolean;
  releaseAt?: string | null;
  releaseAfterDays?: number | null;
  requiresPrevious: boolean;
  requirePassing: boolean;
  /** Student player state. */
  progressStatus?: LessonProgressStatus | null;
  progressPercent?: number | null;
  lock?: LessonLockDto | null;
}

export interface CourseModuleDto extends UnlockRuleFields {
  id: string;
  courseId: string;
  title: string;
  description?: string | null;
  sortOrder: number;
  lessons: LessonSummaryDto[];
}

export interface CourseDetailDto extends CourseListItemDto {
  description?: string | null;
  instructorIds: string[];
  modules: CourseModuleDto[];
  allowSelfEnroll: boolean;
  requireApproval: boolean;
  joinCode?: string | null;
}

export interface CourseOutlineDto extends CourseListItemDto {
  description?: string | null;
  modules: CourseModuleDto[];
  canSelfEnroll: boolean;
  requireApproval: boolean;
  resumeLessonId?: string | null;
}

export interface UpsertCourseDto {
  id?: string | null;
  title: string;
  summary?: string | null;
  description?: string | null;
  coverFileId?: string | null;
  categoryId?: string | null;
  tags: string[];
  level: CourseLevel;
  language: string;
  gradeId?: string | null;
  groupId?: string | null;
  visibility: CourseVisibility;
  ownerId?: string | null;
  instructorIds: string[];
}

export interface UpsertCourseModuleDto extends Partial<UnlockRuleFields> {
  id?: string | null;
  courseId: string;
  title: string;
  description?: string | null;
}

export interface LmsFileDto {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  purpose: string;
  courseId?: string | null;
  learningPathId?: string | null;
}

export interface LessonAttachmentDto {
  id: string;
  fileId: string;
  displayName: string;
  contentType: string;
  sizeBytes: number;
  sortOrder: number;
  /** L6: library item the attachment references. */
  contentItemId?: string | null;
}

/** L6: short reference to the content library item a lesson uses. */
export interface LibraryItemRefDto {
  id: string;
  title: string;
  kind: number;
  scope: number;
}

export interface LinkedItemDto {
  id: string;
  type: LessonType;
  title: string;
  titleAr?: string | null;
  isAvailable: boolean;
  externalUrl?: string | null;
  when?: string | null;
}

export interface LessonDetailDto extends LessonSummaryDto {
  courseId: string;
  contentHtml?: string | null;
  videoSource?: VideoSourceType | null;
  videoUrl?: string | null;
  fileId?: string | null;
  file?: LmsFileDto | null;
  embedUrl?: string | null;
  linkedEntityId?: string | null;
  linkedItem?: LinkedItemDto | null;
  attachments: LessonAttachmentDto[];
  contentItemId?: string | null;
  libraryItem?: LibraryItemRefDto | null;
  progress?: LessonProgressDto | null;
  completionMode: CompletionMode;
  canTrackProgress: boolean;
  videoCompletePercent: number;
}

export interface LessonProgressDto {
  status: LessonProgressStatus;
  percent: number;
  lastPositionSeconds: number;
  watchedSeconds: number;
  durationSeconds?: number | null;
  completedAt?: string | null;
}

export interface UpsertLessonDto {
  id?: string | null;
  moduleId: string;
  title: string;
  type: LessonType;
  contentHtml?: string | null;
  videoSource?: VideoSourceType | null;
  videoUrl?: string | null;
  fileId?: string | null;
  embedUrl?: string | null;
  linkedEntityId?: string | null;
  estimatedMinutes?: number | null;
  isPreview: boolean;
  releaseAt?: string | null;
  releaseAfterDays?: number | null;
  requiresPrevious: boolean;
  requirePassing: boolean;
  /** L6: Video / File / Embed lessons can reference a content library item. */
  contentItemId?: string | null;
}

export interface LookupItemDto {
  id: string;
  nameEn: string;
  nameAr: string;
  parentId?: string | null;
}

export interface CourseFormLookupsDto {
  categories: CourseCategoryDto[];
  grades: LookupItemDto[];
  groups: LookupItemDto[];
  teachers: LookupItemDto[];
}

export interface ReorderLessonsDto {
  courseId: string;
  modules: { moduleId: string; lessonIds: string[] }[];
}

export type LmsFilePurpose = 'cover' | 'video' | 'document' | 'attachment';

// ── L2: enrollment management, progress and reports ────────────────────────
export interface EnrollmentSettingsDto {
  courseId: string;
  allowSelfEnroll: boolean;
  requireApproval: boolean;
  joinCode?: string | null;
  courseStatus: CourseStatus;
  hasLinkedGradeOrGroup: boolean;
  activeCount: number;
  pendingCount: number;
  completedCount: number;
  droppedCount: number;
}

export interface UpdateEnrollmentSettingsDto {
  courseId: string;
  allowSelfEnroll: boolean;
  requireApproval: boolean;
  enableJoinCode: boolean;
}

export interface EnrollmentSearchDto {
  courseId: string;
  status?: EnrollmentStatus | null;
  search?: string | null;
  pageNumber: number;
  pageSize: number;
}

export interface EnrollmentDto {
  id: string;
  studentId: string;
  studentNameEn: string;
  studentNameAr: string;
  email?: string | null;
  source: EnrollmentSource;
  status: EnrollmentStatus;
  requestedAt: string;
  enrolledAt?: string | null;
  completedAt?: string | null;
  droppedAt?: string | null;
  progressPercent: number;
  completedLessons: number;
  lastActivityAt?: string | null;
}

export interface EnrollmentCandidateDto {
  studentId: string;
  nameEn: string;
  nameAr: string;
  email?: string | null;
  gradeNameEn?: string | null;
  gradeNameAr?: string | null;
  enrollmentStatus?: EnrollmentStatus | null;
}

export interface BulkEnrollmentResultDto {
  added: number;
  reactivated: number;
  approved: number;
  rejected: number;
  removed: number;
  skipped: number;
}

export interface SelfEnrollResultDto {
  courseId: string;
  courseTitle: string;
  status: EnrollmentStatus;
}

export interface VideoProgressDto {
  lessonId: string;
  positionSeconds: number;
  durationSeconds: number;
  watchedSeconds: number;
}

export interface LessonProgressResultDto {
  lessonId: string;
  status: LessonProgressStatus;
  percent: number;
  lessonCompletedNow: boolean;
  courseProgressPercent: number;
  completedLessons: number;
  totalLessons: number;
  enrollmentStatus: EnrollmentStatus;
  courseCompletedNow: boolean;
}

export interface ContinueLearningItemDto {
  courseId: string;
  title: string;
  coverFileId?: string | null;
  status: EnrollmentStatus;
  progressPercent: number;
  completedLessons: number;
  totalLessons: number;
  resumeLessonId?: string | null;
  resumeLessonTitle?: string | null;
  lastActivityAt?: string | null;
}

export interface StudentProgressRowDto {
  enrollmentId: string;
  studentId: string;
  studentNameEn: string;
  studentNameAr: string;
  email?: string | null;
  status: EnrollmentStatus;
  source: EnrollmentSource;
  enrolledAt?: string | null;
  progressPercent: number;
  completedLessons: number;
  totalLessons: number;
  lastActivityAt?: string | null;
  completedAt?: string | null;
  lastLessonTitle?: string | null;
}

export interface CourseProgressReportDto {
  courseId: string;
  title: string;
  totalLessons: number;
  averagePercent: number;
  activeCount: number;
  completedCount: number;
  notStartedCount: number;
  rows: StudentProgressRowDto[];
}

export interface ChildCourseProgressDto {
  courseId: string;
  title: string;
  status: EnrollmentStatus;
  progressPercent: number;
  completedLessons: number;
  totalLessons: number;
  enrolledAt?: string | null;
  lastActivityAt?: string | null;
  completedAt?: string | null;
  // M6: what a parent follows up on (ParentCourses/ChildCourses).
  /** The course's linked quizzes, exams and assignments with the child's result, in course order. */
  assessments?: ChildCourseAssessmentDto[];
  /** Average of the graded items' percentages (null until something is graded). */
  averageScorePercent?: number | null;
  /** The first lesson the child hasn't completed (null when everything is done). */
  nextLesson?: ChildCourseNextLessonDto | null;
}

export enum ChildAssessmentStatus {
  NotStarted = 0,
  Submitted = 1,
  PendingReview = 2,
  Graded = 3,
}

export interface ChildCourseAssessmentDto {
  lessonId: string;
  title: string;
  /** LessonType.Quiz, Exam or Assignment. */
  type: LessonType;
  itemId: string;
  status: ChildAssessmentStatus;
  scorePercent?: number | null;
  /** Assignments: the due date. */
  dueAt?: string | null;
  /** Assignments: handed in after the due date. */
  isLate: boolean;
}

export interface ChildCourseNextLessonDto {
  lessonId: string;
  title: string;
  type: LessonType;
  isLocked: boolean;
  lockReason?: LessonLockReason | null;
  /** When a date-based lock opens. */
  availableAt?: string | null;
  /** Assignment lessons: the due date. */
  dueAt?: string | null;
}

// ── L4: engagement & certificates ──────────────────────────────────────────
export type DiscussionFilter = 'all' | 'unanswered' | 'answered' | 'mine';

export interface DiscussionThreadSearchDto {
  courseId: string;
  lessonId?: string | null;
  courseLevelOnly?: boolean;
  search?: string | null;
  filter?: DiscussionFilter;
  pageNumber: number;
  pageSize: number;
}

export interface DiscussionAuthorDto {
  id: string;
  nameEn: string;
  nameAr: string;
  isStaff: boolean;
}

export interface DiscussionThreadListItemDto {
  id: string;
  courseId: string;
  lessonId?: string | null;
  lessonTitle?: string | null;
  title: string;
  preview: string;
  author: DiscussionAuthorDto;
  isPinned: boolean;
  isLocked: boolean;
  isHidden: boolean;
  isAnswered: boolean;
  replyCount: number;
  createdAt: string;
  lastActivityAt: string;
}

export interface DiscussionPostDto {
  id: string;
  threadId: string;
  author: DiscussionAuthorDto;
  body: string;
  isHidden: boolean;
  isAnswer: boolean;
  createdAt: string;
  editedAt?: string | null;
  canEdit: boolean;
  canDelete: boolean;
}

export interface DiscussionThreadDetailDto extends DiscussionThreadListItemDto {
  body: string;
  editedAt?: string | null;
  answeredPostId?: string | null;
  posts: DiscussionPostDto[];
  canReply: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canModerate: boolean;
}

export interface CreateDiscussionThreadDto {
  courseId: string;
  lessonId?: string | null;
  title: string;
  body: string;
}

export interface ModerateThreadDto {
  threadId: string;
  isPinned?: boolean;
  isLocked?: boolean;
  isHidden?: boolean;
}

export interface CourseAnnouncementDto {
  id: string;
  courseId: string;
  title: string;
  body: string;
  isPinned: boolean;
  author: DiscussionAuthorDto;
  publishedAt: string;
  editedAt?: string | null;
  canManage: boolean;
}

export interface UpsertCourseAnnouncementDto {
  id?: string | null;
  courseId: string;
  title: string;
  body: string;
  isPinned: boolean;
  notifyStudents: boolean;
}

export interface CourseReviewDto {
  id: string;
  courseId: string;
  studentId: string;
  studentNameEn: string;
  studentNameAr: string;
  rating: number;
  comment?: string | null;
  createdAt: string;
  editedAt?: string | null;
  response?: string | null;
  respondedAt?: string | null;
  responderNameEn?: string | null;
  responderNameAr?: string | null;
  isHidden: boolean;
  hiddenReason?: string | null;
  isMine: boolean;
}

export interface CourseRatingSummaryDto {
  courseId: string;
  average: number;
  count: number;
  distribution: number[];
  canReview: boolean;
  myReview?: CourseReviewDto | null;
  canRespond: boolean;
  canHide: boolean;
}

export interface CourseReviewSearchDto {
  courseId: string;
  rating?: number | null;
  includeHidden?: boolean;
  pageNumber: number;
  pageSize: number;
}

export enum CertificateOrientation {
  Landscape = 1,
  Portrait = 2,
}

/** Certificate page direction. FollowLanguage lets the backend pick RTL/LTR from the course / school language. */
export enum CertificateDirection {
  FollowLanguage = 0,
  LeftToRight = 1,
  RightToLeft = 2,
}

export enum CertificateSource {
  Automatic = 1,
  Manual = 2,
}

export enum CertificateStatus {
  Active = 1,
  Revoked = 2,
}

export interface CertificateTemplateDto {
  id: string;
  name: string;
  title: string;
  body: string;
  signatureName?: string | null;
  signatureTitle?: string | null;
  primaryColor: string;
  accentColor: string;
  orientation: CertificateOrientation;
  direction: CertificateDirection;
  showLogo: boolean;
  showQrCode: boolean;
  isDefault: boolean;
  courseCount: number;
  issuedCount: number;
  updatedAt: string;
}

export interface UpsertCertificateTemplateDto {
  id?: string | null;
  name: string;
  title: string;
  body: string;
  signatureName?: string | null;
  signatureTitle?: string | null;
  primaryColor: string;
  accentColor: string;
  orientation: CertificateOrientation;
  direction: CertificateDirection;
  showLogo: boolean;
  showQrCode: boolean;
  isDefault: boolean;
}

export interface CertificateTemplateOptionDto {
  id: string;
  name: string;
  isDefault: boolean;
}

export interface CourseCertificateSettingsDto {
  courseId: string;
  certificateEnabled: boolean;
  certificateTemplateId?: string | null;
  defaultTemplateName?: string | null;
  templates: CertificateTemplateOptionDto[];
  activeCount: number;
  revokedCount: number;
  missingCount: number;
}

export interface IssuedCertificateDto {
  id: string;
  /** Null for learning path certificates. */
  courseId?: string | null;
  /** L5: set for learning path certificates (courseTitle then holds the path title). */
  learningPathId?: string | null;
  isLearningPath?: boolean;
  courseTitle: string;
  studentId: string;
  studentNameEn: string;
  studentNameAr?: string | null;
  serialNumber: string;
  verificationCode: string;
  source: CertificateSource;
  status: CertificateStatus;
  issuedAt: string;
  completionDate: string;
  revokedAt?: string | null;
  revokeReason?: string | null;
  tenantNameEn: string;
  tenantNameAr?: string | null;
}

export interface IssueCertificatesResultDto {
  issued: number;
  alreadyIssued: number;
  notEligible: number;
}

export interface CertificateVerificationDto {
  found: boolean;
  isValid: boolean;
  status?: CertificateStatus | null;
  serialNumber?: string | null;
  studentName?: string | null;
  studentNameAr?: string | null;
  courseTitle?: string | null;
  issuerName?: string | null;
  issuerNameAr?: string | null;
  issuedAt?: string | null;
  completionDate?: string | null;
  revokedAt?: string | null;
  isLearningPath?: boolean;
}

// ── L5: learning paths & catalog ───────────────────────────────────────────
export enum CatalogSort {
  Newest = 1,
  Rating = 2,
  Popular = 3,
  Title = 4,
}

/** What the catalog offers the viewer for a course or path (mirrors CatalogRules.DecideAction). */
export enum CatalogAction {
  Preview = 1,
  Enroll = 2,
  RequestToJoin = 3,
  Pending = 4,
  Continue = 5,
  Review = 6,
  Closed = 7,
  Removed = 8,
  Unavailable = 9,
}

export interface LearningPathSearchDto {
  pageNumber: number;
  pageSize: number;
  search?: string | null;
  status?: CourseStatus | null;
  categoryId?: string | null;
}

export interface LearningPathEnrollmentStateDto {
  id: string;
  status: EnrollmentStatus;
  source: EnrollmentSource;
  requestedAt: string;
  enrolledAt?: string | null;
  completedAt?: string | null;
  progressPercent: number;
  completedCourses: number;
  requiredCourses: number;
  nextCourseId?: string | null;
  certificateId?: string | null;
}

export interface LearningPathListItemDto {
  id: string;
  title: string;
  summary?: string | null;
  coverFileId?: string | null;
  categoryId?: string | null;
  categoryNameEn?: string | null;
  categoryNameAr?: string | null;
  tags: string[];
  level: CourseLevel;
  gradeId?: string | null;
  ownerId: string;
  ownerNameEn?: string | null;
  ownerNameAr?: string | null;
  status: CourseStatus;
  visibility: CourseVisibility;
  enforceOrder: boolean;
  courseCount: number;
  requiredCourseCount: number;
  totalLessons: number;
  totalMinutes: number;
  enrolledCount: number;
  averageRating: number;
  ratingCount: number;
  publishedAt?: string | null;
  updatedAt: string;
  canManage: boolean;
  enrollment?: LearningPathEnrollmentStateDto | null;
}

export interface LearningPathCourseDto {
  courseId: string;
  title: string;
  summary?: string | null;
  coverFileId?: string | null;
  status: CourseStatus;
  level: CourseLevel;
  lessonCount: number;
  totalMinutes: number;
  ownerId: string;
  ownerNameEn?: string | null;
  ownerNameAr?: string | null;
  averageRating: number;
  ratingCount: number;
  sortOrder: number;
  isRequired: boolean;
  enrollment?: CourseEnrollmentStateDto | null;
  isLocked: boolean;
  blockingCourseId?: string | null;
  blockingCourseTitle?: string | null;
}

export interface LearningPathDetailDto extends LearningPathListItemDto {
  description?: string | null;
  allowSelfEnroll: boolean;
  requireApproval: boolean;
  certificateEnabled: boolean;
  certificateTemplateId?: string | null;
  courses: LearningPathCourseDto[];
  pendingCount: number;
  completedCount: number;
}

export interface UpsertLearningPathDto {
  id?: string | null;
  title: string;
  summary?: string | null;
  description?: string | null;
  coverFileId?: string | null;
  categoryId?: string | null;
  tags: string[];
  level: CourseLevel;
  gradeId?: string | null;
  visibility: CourseVisibility;
  enforceOrder: boolean;
  allowSelfEnroll: boolean;
  requireApproval: boolean;
  certificateEnabled: boolean;
  certificateTemplateId?: string | null;
  ownerId?: string | null;
}

export interface LearningPathCourseItemDto {
  courseId: string;
  isRequired: boolean;
}

export interface LearningPathCourseOptionDto {
  id: string;
  title: string;
  status: CourseStatus;
  lessonCount: number;
  coverFileId?: string | null;
  ownerNameEn?: string | null;
  ownerNameAr?: string | null;
}

export interface LearningPathFormLookupsDto {
  categories: CourseCategoryDto[];
  grades: LookupItemDto[];
  teachers: LookupItemDto[];
  certificateTemplates: CertificateTemplateOptionDto[];
}

export interface LearningPathEnrollmentSearchDto {
  learningPathId: string;
  status?: EnrollmentStatus | null;
  search?: string | null;
  pageNumber: number;
  pageSize: number;
}

export interface LearningPathEnrollmentDto {
  id: string;
  studentId: string;
  studentNameEn: string;
  studentNameAr: string;
  email?: string | null;
  source: EnrollmentSource;
  status: EnrollmentStatus;
  requestedAt: string;
  enrolledAt?: string | null;
  completedAt?: string | null;
  droppedAt?: string | null;
  progressPercent: number;
  completedCourses: number;
  requiredCourses: number;
  certificateId?: string | null;
  certificateSerial?: string | null;
}

export interface LearningPathEnrollResultDto {
  learningPathId: string;
  title: string;
  status: EnrollmentStatus;
  coursesEnrolled: number;
}

export interface CatalogSearchDto {
  pageNumber: number;
  pageSize: number;
  search?: string | null;
  categoryId?: string | null;
  tags?: string[] | null;
  level?: CourseLevel | null;
  /** Courses only: "ar" | "en" | "ar-en". */
  language?: string | null;
  sort: CatalogSort;
  enrolledOnly?: boolean;
}

export interface CatalogCourseItemDto extends CourseListItemDto {
  enrolledCount: number;
}

export interface CatalogFacetsDto {
  categories: { id: string; nameEn: string; nameAr: string; courseCount: number; pathCount: number }[];
  tags: { tag: string; count: number }[];
  levels: { level: CourseLevel; count: number }[];
  courseCount: number;
  pathCount: number;
}

export interface CatalogPersonDto {
  id: string;
  nameEn: string;
  nameAr: string;
  isOwner: boolean;
}

export interface CatalogPathRefDto {
  id: string;
  title: string;
  courseCount: number;
}

export interface CatalogCourseDetailDto extends CourseOutlineDto {
  instructors: CatalogPersonDto[];
  enrolledCount: number;
  action: CatalogAction;
  paths: CatalogPathRefDto[];
}

export interface CatalogPathDetailDto extends LearningPathListItemDto {
  description?: string | null;
  allowSelfEnroll: boolean;
  requireApproval: boolean;
  certificateEnabled: boolean;
  action: CatalogAction;
  courses: LearningPathCourseDto[];
}
